import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/auth-js';

import { supabase } from '@/lib/supabase';

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  devSignIn?: () => void;
  devSignOut?: () => void;
}

/** Exported for test access — consumers should use the useAuth hook. */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Callback must stay synchronous — Supabase auth-js runs it inside an
    // exclusive lock, so async operations here cause deadlocks.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (event === 'INITIAL_SESSION') {
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const devSignIn = useCallback(() => {
    setSession({
      access_token: 'dev-token',
      refresh_token: 'dev-refresh',
      expires_in: 99999,
      expires_at: Math.floor(Date.now() / 1000) + 99999,
      token_type: 'bearer',
      user: {
        id: 'dev-user-000',
        email: 'dev@pentarchy.club',
        app_metadata: {},
        user_metadata: { full_name: 'Dev User' },
        aud: 'authenticated',
        created_at: new Date().toISOString(),
      },
    } as Session);
    setIsLoading(false);
  }, []);

  const devSignOut = useCallback(() => {
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      ...(__DEV__ ? { devSignIn, devSignOut } : {}),
    }),
    [session, isLoading, devSignIn, devSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
